import { defineStore } from 'pinia'
import { computed, Ref } from 'vue'
import { helpers, required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { PaymentRequestTypeEnum, TimesheetTypeEnum } from '@/components/PaymentRequest/enums'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { useTimesheetStore } from '@/components/PaymentRequest/store/useTimesheetStore'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { useCalculationsStore } from '@/components/PaymentRequest/store/useCalculationsStore'
import { useExpensesStore } from '@/components/PaymentRequest/store/useExpensesStore'
import { useTranslation } from '@/composables/translation'

export const useValidationStore = defineStore('payment-request-validation', () => {
  const { $t } = useTranslation()
  const paymentRequestStore = usePaymentRequestStore()
  const timesheetsStore = useTimesheetStore()
  const timesheetFormStore = useTimesheetFormStore()
  const calculationsStore = useCalculationsStore()
  const expensesStore = useExpensesStore()

  const isExpenseOnlyPaymentRequest = computed(
    () => calculationsStore.totalChargedNumber === 0 && calculationsStore.totalExpensedCents > 0,
  )

  // Validation
  const validationRules = computed(() => ({
    dateStart: {
      required: helpers.withMessage($t('Start date is required'), required),
      notBilled: helpers.withMessage(
        $t('This week is already billed for time. Pick another week.'),
        () => !paymentRequestStore.pickedPaymentRequestPeriodIsBilled,
      ),
    },
    expenseReports: {
      hasNonZeroAmount: helpers.withMessage(
        $t('Each expense report must have a total greater than 0.'),
        (value) => {
          const hasExpenseReports = expensesStore.expenseReports.length > 0
          const allReportsHaveTotal = expensesStore.expenseReports.every(
            (report) => report.total > 0,
          )
          return !hasExpenseReports || allReportsHaveTotal
        },
      ),
      hasAttachments: helpers.withMessage(
        $t('Each expense must have file attachments.'),
        (value) => {
          const hasExpenseReports = expensesStore.expenseReports.length > 0
          const allReportsHaveFiles = expensesStore.expenseReports.every((report) => report.expenses.every((expense) => expense.files.length > 0))
          return !hasExpenseReports || allReportsHaveFiles
        },
      ),
    },
    // All timesheet, amount, and date validations are bypassed by attaching expenses
    // unless the billed amount on the payment request is > 0
    dateEnd: {
      requiredWhenFixedAmount: helpers.withMessage(
        $t('End date is required'),
        (value) => isExpenseOnlyPaymentRequest.value || !!value,
      ),
    },
    milestoneId: {
      requiredWhenMilestone: helpers.withMessage(
        $t('Please select a milestone'),
        (value: string | null) =>
          paymentRequestStore.paymentRequestType !== PaymentRequestTypeEnum.MILESTONE ||
          (paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.MILESTONE && !!value),
      ),
    },
    billableTime: {
      requiredWhenTimeXRate: helpers.withMessage(
        expensesStore.enableExpenseReports
          ? $t('Add billable time. Or attach an expense for an expense-only request.')
          : $t('Add billable time'),
        (value: number) =>
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT ||
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.MILESTONE ||
          value > 0 ||
          !!timesheetsStore.worksomeTimesheetId ||
          calculationsStore.totalExpensedCents > 0,
      ),
    },
    billableTotal: {
      requiredWhenFixedAmount: helpers.withMessage(
        expensesStore.enableExpenseReports
          ? $t('Add a billable total or attach expenses')
          : $t('Add billable total'),
        (value: number) =>
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.TIME_X_RATE ||
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.MILESTONE ||
          value > 0 ||
          calculationsStore.totalExpensedCents > 0,
      ),
    },
    timesheetType: {
      cannotSelectNoneIfTimesheetsRequired: helpers.withMessage(
        $t('Timesheet is required'),
        () =>
          !timesheetsStore.requireTimesheets ||
          isExpenseOnlyPaymentRequest.value ||
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.MILESTONE ||
          (timesheetsStore.requireTimesheets &&
            paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT) ||
          timesheetsStore.timesheetType.value !== TimesheetTypeEnum.NONE,
      ),
    },
    worksomeTimesheetValue: {
      requiredIf: helpers.withMessage(
        $t('Timesheet must include at least one entry'),
        (value: string): boolean =>
          !timesheetsStore.requireTimesheets ||
          isExpenseOnlyPaymentRequest.value ||
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.MILESTONE ||
          (timesheetsStore.requireTimesheets &&
            paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT) ||
          timesheetsStore.timesheetType.value !== TimesheetTypeEnum.TIMESHEET ||
          (Boolean(timesheetsStore.worksomeTimesheetId) && Boolean(value)),
      ),
    },
    timesheetSpreadsheetValue: {
      requiredIf: helpers.withMessage(
        $t('Timesheet is required'),
        (value: string) =>
          !timesheetsStore.requireTimesheets ||
          isExpenseOnlyPaymentRequest.value ||
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.MILESTONE ||
          (timesheetsStore.requireTimesheets &&
            paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT) ||
          timesheetsStore.timesheetType.value !== TimesheetTypeEnum.SHEET ||
          !!value.length,
      ),
    },
    timesheetFiles: {
      requiredIf: helpers.withMessage(
        $t('Timesheet is required'),
        (value: []) =>
          !timesheetsStore.requireTimesheets ||
          isExpenseOnlyPaymentRequest.value ||
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.MILESTONE ||
          (timesheetsStore.requireTimesheets &&
            paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT) ||
          timesheetsStore.timesheetType.value !== TimesheetTypeEnum.FILE ||
          !!value.length,
      ),
    },
    timesheetComment: {
      requiredIf: helpers.withMessage(
        $t('Timesheet is required'),
        (value: string) =>
          !timesheetsStore.requireTimesheets ||
          isExpenseOnlyPaymentRequest.value ||
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.MILESTONE ||
          (timesheetsStore.requireTimesheets &&
            paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT) ||
          timesheetsStore.timesheetType.value !== TimesheetTypeEnum.TEXT ||
          !!value.length,
      ),
    },
    timesheetConfirmed: {
      requiredIf: helpers.withMessage(
        $t('Timesheet confirmation is required'),
        (value: string) =>
          !timesheetsStore.requireTimesheetConfirmation ||
          isExpenseOnlyPaymentRequest.value ||
          paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.MILESTONE ||
          (timesheetsStore.requireTimesheets &&
            paymentRequestStore.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT) ||
          !!value,
      ),
    },
  }))
  const combinedForm = computed(() => ({
    timesheetSpreadsheetValue: timesheetsStore.timesheetSpreadsheetValue,
    timesheetFiles: timesheetsStore.timesheetFiles,
    timesheetComment: timesheetsStore.timesheetComment,
    worksomeTimesheetValue: timesheetFormStore.totalPayableTime,
    ...paymentRequestStore.paymentRequestForm,
  }))

  const v$ = useVuelidate(validationRules, combinedForm as Ref, {
    $lazy: true,
    $autoDirty: true,
  })

  return { v$ }
})
